import { Injectable, Injector } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { EMPTY } from 'rxjs';
import { map, mergeMap, catchError, finalize } from 'rxjs/operators';
import { NesSessionService } from '@deltager/organizers/admin/modules/event-sessions/shared/nes-session.service';
import {
  GetSessionNamesSuccessAction,
  GetSessionSuccessAction,
  LoadEventSessionsAction,
  LoadEventSessionsSuccessAction,
  SessionsActionTypes,
  SetPriceSessionSetupSuccessAction,
} from '@deltager/organizers/admin/modules/event-sessions/store/sessions.actions';
import { SpinnerOverlayService } from '@deltager/organizers/admin/shared/spinner-overlay/spinner-overlay.service';
import { TranslateService } from '@ngx-translate/core';
import { AppNotificationsService } from '@deltager/organizers/admin/core/services/app-notifications.service';

@Injectable()
export class SessionsEffects {
  constructor(
    private actions$: Actions,
    private spinner: SpinnerOverlayService,
    private nesSessionService: NesSessionService,
    private injector: Injector,
    private notificationService: AppNotificationsService,
  ) {}

  private get translateService(): TranslateService {
    return this.injector.get(TranslateService);
  }

  loadSessions$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SessionsActionTypes.LoadEventSessions),
      mergeMap((a: any) => {
        this.spinner.show();
        return this.nesSessionService.getEventSessionsList(a.payload.eventId, a.payload.from, a.payload.to).pipe(
          map(s => {
            this.spinner.hide();
            return new LoadEventSessionsSuccessAction(s);
          }),
          catchError(() => {
            this.spinner.hide();
            return EMPTY;
          }),
        );
      }),
      finalize(() => this.spinner.hide()),
    ),
  );

  setPriceSessionSetup$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SessionsActionTypes.SetPriceSessionSetup),
      mergeMap((a: any) => {
        this.spinner.show();
        return this.nesSessionService.setPriceSessionSetup(a.payload.priceId, a.payload.eventId, a.payload.ps).pipe(
          map(s => {
            this.spinner.hide();
            return new SetPriceSessionSetupSuccessAction();
          }),
          catchError(() => {
            this.spinner.hide();
            return EMPTY;
          }),
        );
      }),
      finalize(() => this.spinner.hide()),
    ),
  );

  saveSession$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SessionsActionTypes.SaveSession),
      mergeMap((a: any) => {
        this.spinner.show();
        return this.nesSessionService.addSession(a.payload.session, a.payload.eventId).pipe(
          map(() => {
            this.spinner.hide();

            this.notificationService.sendSuccess({
              message: this.translateService.instant('sessions.session_added_toast'),
            });

            return new LoadEventSessionsAction({
              eventId: a.payload.eventId,
              from: a.payload.from,
              to: a.payload.to,
            });
          }),
          catchError(() => {
            this.spinner.hide();
            return EMPTY;
          }),
        );
      }),
      finalize(() => this.spinner.hide()),
    ),
  );

  editSession$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SessionsActionTypes.EditSession),
      mergeMap((a: any) => {
        this.spinner.show();
        return this.nesSessionService.patchSession(a.payload.session, a.payload.eventId).pipe(
          map(s => {
            this.spinner.hide();

            this.notificationService.sendSuccess({
              message: this.translateService.instant('sessions.session_updated_toast'),
            });

            return new LoadEventSessionsAction({
              eventId: a.payload.eventId,
              from: a.payload.from,
              to: a.payload.to,
            });
          }),
          catchError(() => {
            this.spinner.hide();
            return EMPTY;
          }),
        );
      }),
      finalize(() => this.spinner.hide()),
    ),
  );

  deleteSession$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SessionsActionTypes.DeleteSession),
      mergeMap((a: any) => {
        this.spinner.show();
        return this.nesSessionService.deleteSession(a.payload.session, a.payload.eventId).pipe(
          map(s => {
            this.spinner.hide();

            this.notificationService.sendSuccess({
              message: this.translateService.instant('sessions.session_deleted_toast'),
            });

            return new LoadEventSessionsAction({
              eventId: a.payload.eventId,
              from: a.payload.from,
              to: a.payload.to,
            });
          }),
          catchError(() => {
            this.spinner.hide();
            return EMPTY;
          }),
        );
      }),
      finalize(() => this.spinner.hide()),
    ),
  );

  getSession$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SessionsActionTypes.GetSession),
      mergeMap((a: any) => {
        this.spinner.show();
        return this.nesSessionService.getSession(a.payload.session, a.payload.eventId).pipe(
          map(s => {
            this.spinner.hide();
            return new GetSessionSuccessAction({
              session: s,
            });
          }),
          catchError(() => {
            this.spinner.hide();
            return EMPTY;
          }),
        );
      }),
      finalize(() => this.spinner.hide()),
    ),
  );

  getSessionsNames$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SessionsActionTypes.GetSessionsNames),
      mergeMap((a: any) => {
        this.spinner.show();
        return this.nesSessionService.getSessionNames(a.payload.eventId).pipe(
          map(s => {
            this.spinner.hide();
            return new GetSessionNamesSuccessAction({ sessionsList: s });
          }),
          catchError(() => {
            this.spinner.hide();
            return EMPTY;
          }),
        );
      }),
    ),
  );

  batchUpdateSession$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SessionsActionTypes.BatchEditSession),
      mergeMap((a: any) => {
        this.spinner.show();
        return this.nesSessionService.batchUpdateSession(a.payload.session).pipe(
          map(s => {
            this.spinner.hide();

            this.notificationService.sendSuccess({
              message: this.translateService.instant('sessions.sessions_updated_toast'),
            });

            return new LoadEventSessionsAction({
              eventId: a.payload.eventId,
              from: a.payload.from,
              to: a.payload.to,
            });
          }),
          catchError(() => {
            this.spinner.hide();
            return EMPTY;
          }),
        );
      }),
    ),
  );

  batchDeleteSession$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SessionsActionTypes.BatchDeleteSession),
      mergeMap((a: any) => {
        this.spinner.show();
        return this.nesSessionService.batchDeleteSession(a.payload.sessionNames, a.payload.eventId).pipe(
          map(s => {
            this.spinner.hide();

            this.notificationService.sendSuccess({
              message: this.translateService.instant('sessions.sessions_deleted_toast'),
            });

            return new LoadEventSessionsAction({
              eventId: a.payload.eventId,
              from: a.payload.from,
              to: a.payload.to,
            });
          }),
          catchError(() => {
            this.spinner.hide();
            return EMPTY;
          }),
        );
      }),
    ),
  );
}
